import { memo, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';
import {
  CollapsiblePanel,
  EditWorkflowType,
  FlowBuilder2TaskFieldNamesEnum,
  HeaderWithScrollables,
  IMenuAction,
  Menu,
  Skeleton,
  TextBadge,
  useAssistantRequest,
  useFlowBuilder2ActionValidators,
  useEditorStore,
  useNameField,
  IValidationResult,
  useEvents,
  Events,
  useAssistantLoadingStore,
} from '@diana/core';
import { MoreHorizRounded as MenuIcon } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, Stack, Typography, useTheme } from '@mui/material';
import { useFlowBuilder2PageStore } from '../../../Store/useFlowBuilder2PageStore';
import { useTaskMenuActions } from '../../../Support/useTaskMenuActions';
import { useEntityFieldState } from '../../../Support/useEntityFieldState';
import { TaskNameField } from '../../../Components/TaskNameField/TaskNameField';
import { NameFieldWrapper } from '../../../Components/NameFieldWrapper/NameFieldWrapper';
import { ValidationTip } from '../../../Components/ValidationTip/ValidationTip';
import { LayoutBuilder } from '../LayoutBuilder/LayoutBuilder';
import { useFindTask } from '../../../Support/useFindTask';
import { ITaskBuilderProps } from './TaskBuilder.types';
import { FLOW_BUILDER2_PAGE_NS } from '../../../i18n';
import { useLayoutAcceptRejectObserver } from '../../../Support/useLayoutAcceptRejectObserver';
import { FocusWrapper } from '../../../FlowBuilder2Page.styles';

export const TaskBuilder = memo((props: ITaskBuilderProps) => {
  const { taskId, stepId, flowId } = props;

  const findTask = useFindTask(taskId);
  const actionValidators = useFlowBuilder2ActionValidators();

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [isHovered, setIsHovered] = useState<boolean>(false);

  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);
  const { palette, typography } = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);
  const taskContainerRef = useRef<HTMLElement | null>(null);

  const setActiveTaskId = useFlowBuilder2PageStore((s) => s.setActiveTaskId);
  const isActiveTask = useFlowBuilder2PageStore((s) => s.activeTaskId === taskId);
  const isCollapsed = useFlowBuilder2PageStore((s) => s.collapsedTasksMap[taskId]);
  const setTaskCollapsed = useFlowBuilder2PageStore((s) => s.setTaskCollapsed);
  const aiPendingStatus = useFlowBuilder2PageStore((s) => s.aiPendingStatuses);
  const hasAnyPendingStatus = useFlowBuilder2PageStore((s) => s.hasAnyPendingStatus(), isEqual);

  const isChatLoading = useAssistantLoadingStore((state) => state.isLoading);

  const task = useEditorStore((s) => findTask(s.flow), isEqual);

  const disabled = hasAnyPendingStatus || isChatLoading;
  const isRestricted = task?.isRestricted ?? '';
  const displayName = task?.taskDisplayName ?? '';
  const layoutId = task?.layoutId ?? '';

  const actionsValidation = useEditorStore((s): IValidationResult | null => {
    const flow = s.getFlow();
    const task = findTask(flow);
    if (flow && task && Array.isArray(task.actions)) {
      for (const action of task.actions) {
        for (const validate of Array.from(Object.values(actionValidators))) {
          const error = validate({ flow, actionSet: task, action });
          if (error) return error;
        }
      }
    }
    return null;
  });

  const storeParams = {
    type: EditWorkflowType.Layout,
    flowId: flowId ?? '',
    layoutId: layoutId ?? '',
    componentId: undefined,
  };

  const { hasRequest } = useAssistantRequest(storeParams);
  useLayoutAcceptRejectObserver({ ref: taskContainerRef, layoutId });

  useEvents((ctx) => {
    Events.SetFlowBuilderActiveTask.attach(ctx, ({ layoutId: layoutIdFromProps }) => {
      if (layoutIdFromProps === layoutId && !isActiveTask) {
        setActiveTaskId(taskId);
      }
    });
  }, []);

  const hasPendingStatus =
    aiPendingStatus.layouts.includes(layoutId) && aiPendingStatus.flows.length === 0;
  const fieldState = useEntityFieldState({
    objectId: taskId,
    objectType: 'task',
    fieldName: FlowBuilder2TaskFieldNamesEnum.DisplayName,
  });
  const isLabelError = fieldState.validationResult?.type === 'error';

  const openTaskEditor = useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e.stopPropagation();
      if (taskId && !isActiveTask) {
        setActiveTaskId(taskId);
        setTaskCollapsed(taskId, false);
      }
    },
    [taskId, isActiveTask, setActiveTaskId, setTaskCollapsed],
  );

  const renameTask = useCallback(() => {
    setTimeout(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        const length = el.value.length;
        el.setSelectionRange(length, length);
      }
    }, 0);
  }, []);

  const {
    onClick: handleLabelClick,
    onDoubleClick: handleLabelDoubleClick,
    onFocus,
    onBlur,
    onKeyDown,
    isRenaming,
  } = useNameField({
    onClick: (e) => openTaskEditor(e),
    onDoubleClick: renameTask,
    isError: isLabelError,
  });

  const titleFontSize = '16px';

  const menuActions = useTaskMenuActions({ taskId, stepId });

  const allMenuActions = useMemo<IMenuAction[]>(() => {
    return [
      {
        title: t('titles.rename'),
        onClick: ({ close }) => {
          close();
          onFocus();
          renameTask();
        },
      },
      ...menuActions,
    ];
  }, [menuActions, renameTask, onFocus, t]);

  useLayoutEffect(() => {
    return () => setTaskCollapsed(taskId, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  return (
    <Box
      data-task-id={taskId}
      ref={taskContainerRef}
      onMouseEnter={() => !isActiveTask && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <FocusWrapper
        isPendingStatus={hasPendingStatus}
        isActive={isActiveTask}
        isHovered={isHovered}
        onClick={openTaskEditor}
        sx={{
          my: 0,
          cursor: isActiveTask ? 'auto' : 'pointer',
          '.CollapsiblePanel-header': { py: 0.75, pl: 1 },
          '.MuiCollapse-wrapperInner > .MuiPaper-root': { pt: 0 },
          'button[data-testid="CollapsiblePanelHeader-toggleButton"]': {
            p: 0.75,
            svg: { fontSize: 20 },
          },
        }}
      >
        <CollapsiblePanel
          collapse={isCollapsed}
          setCollapse={(collapse) => setTaskCollapsed(taskId, collapse)}
          showCollapseButton
          showHeaderHighlight
          showOverflow={isRenaming}
          minHeight={36}
          header={
            <HeaderWithScrollables
              scroll={false}
              left={
                isRenaming ? (
                  <TaskNameField
                    key={taskId}
                    fullWidth
                    multiline
                    data-task-name-field
                    variant="standard"
                    taskId={taskId}
                    stepId={stepId}
                    onClick={(e) => e.stopPropagation()}
                    disabled={disabled || hasRequest}
                    onBlur={onBlur}
                    onFocus={onFocus}
                    onKeyDown={onKeyDown}
                    fontSize={titleFontSize}
                    inputRef={inputRef}
                    InputProps={{ sx: { p: 0 } }}
                    inputProps={{
                      sx: {
                        minWidth: '250px',
                        fontSize: titleFontSize,
                        borderRadius: 1.5,
                        backgroundColor: palette.background.card,
                      },
                    }}
                    sx={{
                      top: 2,
                      '.MuiInput-root:before': { borderColor: 'transparent' },
                      '.MuiInputBase-input, .MuiInputBase-input::placeholder': {
                        fontWeight: typography.fontWeightMedium,
                      },
                    }}
                  />
                ) : (
                  <NameFieldWrapper
                    disabled={disabled || hasRequest}
                    onDoubleClick={handleLabelDoubleClick}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLabelClick(e);
                    }}
                  >
                    <Typography
                      sx={{
                        ml: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 250,
                        color: !displayName ? palette.text.placeholder : palette.text.primary,
                        fontWeight: 500,
                        fontSize: 16,
                      }}
                    >
                      {!displayName ? t('labels.untitledTask') : displayName}
                    </Typography>
                  </NameFieldWrapper>
                )
              }
              right={
                <Stack direction="row" alignItems="center" gap={1}>
                  {!!actionsValidation && (
                    <ValidationTip
                      type={actionsValidation.type}
                      message={actionsValidation.message}
                    />
                  )}
                  {isRestricted && (
                    <TextBadge color="tango">{t('labels.restrictedTask')}</TextBadge>
                  )}
                  <IconButton
                    size="small"
                    sx={{ p: 0.5 }}
                    disabled={disabled || hasRequest}
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuAnchor(e.currentTarget);
                    }}
                  >
                    {menuActions.some((a) => a.loading) ? (
                      <CircularProgress size="1em" />
                    ) : (
                      <MenuIcon sx={{ width: 24, height: 24 }} />
                    )}
                  </IconButton>
                </Stack>
              }
            />
          }
        >
          {hasRequest ? (
            <div data-testid="builder-task-skeleton-loader">
              <Skeleton count={3} />
            </div>
          ) : (
            <LayoutBuilder layoutId={layoutId} taskId={taskId} stepId={stepId} flowId={flowId} />
          )}
        </CollapsiblePanel>
      </FocusWrapper>

      <Menu
        open={!!menuAnchor}
        anchorEl={menuAnchor}
        onClose={() => setMenuAnchor(null)}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        actions={allMenuActions}
      />
    </Box>
  );
}, isEqual);
